// Lógica de presentación de los resultados del buscador (findObject.vue):
// formato de documentos, resaltado de coincidencias y orden por relevancia.
// Son funciones puras: no dependen del componente ni tocan los datos.

// Campos que identifican al registro: a igual coincidencia, ganan sobre el
// resto (ej.: un código exacto antes que un nombre que lo contiene). Para
// columnas anidadas ("business.reference") cuenta el último tramo.
const IDENTIFIER_KEYS = ['id', 'code', 'cuit', 'barcode', 'supplier_code', 'reference', 'point_sale']

// Campos que siempre se comparan si el registro los trae, aunque el buscador
// no los tenga como columna. 'id' no está: solo cuenta si su columna está
// disponible (ver setFieldTable), porque en ese caso el backend tampoco
// busca por id.
const EXTRA_SEARCH_KEYS = ['code', 'name', 'cuit']

// La columna "cuit" trae el documento del registro (CUIT, DNI...); el
// backend envía su tipo en document_type.
export function isDocumentKey(key) {
  return String(key || '').toLowerCase() === 'cuit'
}

// Minúsculas y sin acentos, carácter a carácter: conserva el largo del texto
// original para poder usar los índices del match sobre él.
export function foldText(text) {
  return Array.from(String(text)).map(char => {
    var base = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    return base.length === char.length ? base : char
  }).join('')
}

// Formatea un documento para mostrarlo; el dato guardado no cambia:
//   CUIT / CUIL / CDI (11 dígitos) -> xx-xxxxxxxx-x
//   DNI / LE / LC (7 u 8 dígitos)  -> xx.xxx.xxx
// Sin tipo informado se decide por la cantidad de dígitos. Cualquier otro
// caso (pasaporte, CI, dato incompleto) se devuelve tal cual.
export function formatDocument(value, documentType) {
  if(value === null || value === undefined) return value

  var digits = String(value).replace(/\D/g, '')
  var type = documentType ? String(documentType).trim().toUpperCase() : ''
  var isTaxId = type ? ['CUIT', 'CUIL', 'CDI'].indexOf(type) !== -1 : true
  var isPersonalId = type ? ['DNI', 'LE', 'LC'].indexOf(type) !== -1 : true

  if(isTaxId && digits.length === 11) {
    return digits.slice(0, 2) + '-' + digits.slice(2, 10) + '-' + digits.slice(10)
  }
  if(isPersonalId && (digits.length === 7 || digits.length === 8)) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
  return value
}

// Parte el texto de una celda en tramos { text, match } según las palabras
// del término buscado (sin distinguir mayúsculas ni acentos). Con
// digitsOnly (documentos) se comparan solo los dígitos, así "2012" marca el
// inicio de 20-12345678-9 y los separadores internos quedan dentro de la
// marca.
export function highlightParts(value, term, options) {
  var digitsOnly = Boolean(options && options.digitsOnly)
  var text = value === null || value === undefined ? '' : String(value)
  var terms = foldText(term || '').split(/\s+/).filter(Boolean)
  if(!text || !terms.length) return [{ text: text, match: false }]

  var marked = new Array(text.length).fill(false)

  if(digitsOnly) {
    var digits = ''
    var positions = []
    for(var d = 0; d < text.length; d++) {
      if(/\d/.test(text[d])) { digits += text[d]; positions.push(d) }
    }
    terms.map(word => word.replace(/\D/g, '')).filter(Boolean).forEach(word => {
      var from = 0
      var at
      while((at = digits.indexOf(word, from)) !== -1) {
        for(var k = positions[at]; k <= positions[at + word.length - 1]; k++) marked[k] = true
        from = at + word.length
      }
    })
  } else {
    var folded = foldText(text)
    if(folded.length !== text.length) return [{ text: text, match: false }]

    terms.forEach(word => {
      var from = 0
      var at
      while((at = folded.indexOf(word, from)) !== -1) {
        for(var i = at; i < at + word.length; i++) marked[i] = true
        from = at + word.length
      }
    })
  }

  var parts = []
  for(var i = 0; i < text.length; i++) {
    var last = parts[parts.length - 1]
    if(last && last.match === marked[i]) last.text += text[i]
    else parts.push({ text: text[i], match: marked[i] })
  }
  return parts
}

// Valor de una columna del registro. Acepta claves anidadas ("business.name"),
// igual que la tabla de resultados; devuelve undefined si no existe.
function columnValue(item, key) {
  if(!item) return undefined
  if(item[key] !== undefined) return item[key]
  if(String(key).indexOf('.') === -1) return undefined
  return String(key).split('.').reduce((current, part) => (current === null || current === undefined) ? undefined : current[part], item)
}

function isIdentifierKey(key) {
  var parts = String(key).split('.')
  return IDENTIFIER_KEYS.indexOf(parts[parts.length - 1]) !== -1
}

// Qué tan bien aparece una palabra dentro del texto (menor = mejor); null si
// no está. Se toma la mejor aparición:
//   0 = palabra entera al inicio del texto      ("15 mm cable")
//   1 = palabra entera en otro lugar            ("cable 15 mm")
//   2 = comienzo de una palabra, al inicio      ("150 cable")
//   3 = comienzo de una palabra, en otro lugar  ("cable 150")
//   4 = dentro de una palabra                   ("profesional" para "ion")
function wordScore(text, word) {
  var best = null
  var from = 0
  var at
  while((at = text.indexOf(word, from)) !== -1) {
    var atStart = at === 0
    var wordStart = atStart || /[^a-z0-9]/.test(text.charAt(at - 1))
    var end = at + word.length
    var wordEnd = end >= text.length || /[^a-z0-9]/.test(text.charAt(end))
    var score = !wordStart ? 4 : ((wordEnd ? 0 : 2) + (atStart ? 0 : 1))
    if(best === null || score < best) best = score
    if(best === 0) break
    from = at + 1
  }
  return best
}

// Ordena los resultados de la coincidencia más exacta a la menos exacta. El
// backend solo filtra (LIKE '%término%'), no ordena. Para cada registro se
// toma su mejor coincidencia entre los campos que se pueden buscar (los de
// options.keys, más código, nombre, CUIT y options.display):
//   0 = el campo es igual al término   (código 410 con "410")
//   1 = el campo empieza con el término
//   2 = una palabra del campo empieza con el término
//   3 = el término está contenido en algún punto del campo
//   4 = el campo contiene todas las palabras del término, por separado
//       (ej.: "dest ot"); se afina con qué tan bien aparece cada palabra
//       (entera > comienzo de palabra > dentro de una palabra, ver wordScore)
//   5 = sin coincidencia visible (ej.: matcheó por un dato que no viene en
//       el resultado)
// A igual nivel se compara (en este orden): la posición de las palabras en
// el nivel 4, el campo más identificatorio (id/código antes que nombre), el
// valor más corto (más parecido al término) y, por último, se conserva el
// orden original del backend. Devuelve un arreglo nuevo con los mismos
// registros: nunca descarta ni modifica ninguno.
export function sortByRelevance(items, term, options) {
  var needle = foldText((term || '').trim())
  if(!Array.isArray(items) || items.length < 2 || !needle) return items

  var keys = ((options && options.keys) || []).slice()
  EXTRA_SEARCH_KEYS.forEach(key => { if(keys.indexOf(key) === -1) keys.push(key) })
  var display = options && options.display
  if(display && keys.indexOf(display) === -1) keys.push(display)

  var words = needle.split(/\s+/).filter(Boolean)

  // El documento (CUIT/DNI) se compara solo por dígitos (con o sin
  // separadores, tanto en el dato como en lo escrito).
  var documentNeedle = needle.replace(/\D/g, '')
  var documentWords = words.map(word => word.replace(/\D/g, '')).filter(Boolean)

  var rankValue = (value, isDocument) => {
    var text = isDocument ? String(value).replace(/\D/g, '') : foldText(String(value).trim())
    var target = isDocument ? documentNeedle : needle
    var targetWords = isDocument ? documentWords : words
    if(!text || !target) return null
    if(text === target) return { level: 0, spread: 0 }
    var at = text.indexOf(target)
    if(at === 0) return { level: 1, spread: 0 }
    if(at > 0) return { level: /[^a-z0-9]/.test(text.charAt(at - 1)) ? 2 : 3, spread: 0 }
    if(targetWords.length > 1) {
      var spread = 0
      for(var i = 0; i < targetWords.length; i++) {
        var position = wordScore(text, targetWords[i])
        if(position === null) return null
        spread += position
      }
      return { level: 4, spread: spread }
    }
    return null
  }

  // negativo si "a" es mejor coincidencia que "b"
  var compare = (a, b) =>
    (a.level - b.level) ||
    (a.spread - b.spread) ||
    (a.priority - b.priority) ||
    (a.length - b.length)

  var scored = items.map((item, position) => {
    var best = { level: 5, spread: 0, priority: 2, length: 0 }
    keys.forEach(key => {
      var value = columnValue(item, key)
      if(value === null || value === undefined || typeof value === 'object') return
      var rank = rankValue(value, isDocumentKey(key))
      if(rank === null) return
      var candidate = {
        level: rank.level,
        spread: rank.spread,
        priority: isIdentifierKey(key) ? 0 : 1,
        length: String(value).length,
      }
      if(compare(candidate, best) < 0) best = candidate
    })
    return { item: item, position: position, best: best }
  })

  scored.sort((a, b) => compare(a.best, b.best) || (a.position - b.position))

  return scored.map(entry => entry.item)
}
